import type { TGuaranteesContractOption } from './types.ts';

const normalizeContractValue = (value: unknown): string => String(value ?? '').trim();

const normalizeContractComparableValue = (value: unknown): string => {
  const normalizedValue = normalizeContractValue(value);
  const isoDateMatch = normalizedValue.match(/^\d{4}-\d{2}-\d{2}/);
  const displayDateMatch = normalizedValue.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);

  if (isoDateMatch) {
    return isoDateMatch[0].toUpperCase();
  }

  if (displayDateMatch) {
    return `${displayDateMatch[3]}-${displayDateMatch[2]}-${displayDateMatch[1]}`;
  }

  return normalizedValue.toUpperCase();
};

const getGuaranteesContractFallbackIdentityKey = (option: TGuaranteesContractOption): string => {
  const fallbackKey = [
    option.mainDealNumber,
    option.mainDealDate,
    option.mainDealDateFrom,
  ]
    .map(normalizeContractComparableValue)
    .filter(Boolean)
    .join('|');

  return fallbackKey ? `fields:${fallbackKey}` : '';
};

export const getGuaranteesContractIdentityKey = (option: TGuaranteesContractOption): string => {
  const fallbackKey = getGuaranteesContractFallbackIdentityKey(option);
  const normalizedMainDealId = normalizeContractComparableValue(option.mainDealId);

  return (normalizedMainDealId ? `id:${normalizedMainDealId}` : '') || fallbackKey;
};

export const areGuaranteesContractNumbersEqual = (
  firstOption: TGuaranteesContractOption,
  secondOption: TGuaranteesContractOption
): boolean => {
  const firstNumber = normalizeContractComparableValue(firstOption.mainDealNumber);
  const secondNumber = normalizeContractComparableValue(secondOption.mainDealNumber);

  return !!firstNumber && firstNumber === secondNumber;
};

export const areGuaranteesContractIdentitiesEqual = (
  firstOption: TGuaranteesContractOption,
  secondOption: TGuaranteesContractOption
): boolean => {
  const firstId = normalizeContractComparableValue(firstOption.mainDealId);
  const secondId = normalizeContractComparableValue(secondOption.mainDealId);

  if (firstId && secondId) {
    return firstId === secondId;
  }

  const firstKey = getGuaranteesContractFallbackIdentityKey(firstOption);
  const secondKey = getGuaranteesContractFallbackIdentityKey(secondOption);

  if (firstKey && secondKey) {
    return firstKey === secondKey;
  }

  return false;
};
